<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useScrollAnimations } from '@/composables/useScrollAnimations';
import rvLogo from '@/assets/RVLogoV2.png';

const { animateInView, slideUp } = useScrollAnimations();
const sectionRef = ref(null);
const cardsRef = ref([]);

const { t } = useI18n();

const testimonials = computed(() => [
    {
        title: t('landingPage.v2.testimonials.items[0].title'),
        intro: t('landingPage.v2.testimonials.items[0].intro'),
        projectDetails: t('landingPage.v2.testimonials.items[0].projectDetails'),
        text: t('landingPage.v2.testimonials.items[0].text'),
        name: t('landingPage.v2.testimonials.items[0].name'),
        company: t('landingPage.v2.testimonials.items[0].company'),
        image: rvLogo,
        avatar: null
    },
    {
        title: t('landingPage.v2.testimonials.items[1].title'),
        intro: t('landingPage.v2.testimonials.items[1].intro'),
        projectDetails: t('landingPage.v2.testimonials.items[1].projectDetails'),
        text: t('landingPage.v2.testimonials.items[1].text'),
        name: t('landingPage.v2.testimonials.items[1].name'),
        company: t('landingPage.v2.testimonials.items[1].company'),
        image: rvLogo,
        avatar: null
    }
]);

onMounted(() => {
    if (sectionRef.value) {
        animateInView(sectionRef.value, () => {
            cardsRef.value.forEach((card, index) => {
                slideUp(card, index * 0.2);
            });
        });
    }
});
</script>

<template>
    <div class="py-24 relative" ref="sectionRef">
        <!-- Background elements if needed -->
        <div class="mx-auto max-w-7xl px-6 lg:px-8">

            <!-- Header -->
            <div class="text-center mb-16">
                <h2 class="text-3xl font-bold tracking-tight text-white sm:text-5xl"
                    v-html="$t('landingPage.v2.testimonials.header')">
                </h2>
            </div>

            <!-- Testimonials Grid -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
                <div v-for="(testimonial, index) in testimonials" :key="index" ref="cardsRef"
                    class="bg-gray-900/50 border border-white/20 rounded-2xl p-8 md:p-10 flex flex-col opacity-0 hover:border-terciary/50 transition-colors duration-300">

                    <h3 class="text-xl font-bold text-white mb-2">{{ testimonial.title }}</h3>
                    <p class="text-white font-medium mb-6">{{ testimonial.intro }}</p>

                    <div class="mb-4">
                        <strong class="text-white block mb-2">{{ testimonial.projectDetails }}</strong>
                        <p class="text-gray-400 leading-relaxed">{{ testimonial.text }}</p>
                    </div>

                    <div class="mt-auto pt-8 flex items-center gap-4">
                        <!-- Custom Avatar Icon from Design -->
                        <div class="bg-white rounded-full p-1 w-12 h-12 flex items-center justify-center shrink-0">
                            <!-- Placeholder icon matching the design's "B" logo or similar functionality -->
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"
                                class="w-8 h-8 text-black">
                                <path fill-rule="evenodd"
                                    d="M7.5 6a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM3.751 20.105a8.25 8.25 0 0116.498 0 .75.75 0 01-.437.695A18.683 18.683 0 0112 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 01-.437-.695z"
                                    clip-rule="evenodd" />
                            </svg>
                        </div>
                        <!-- Company Logo -->
                        <div class="h-8 w-px bg-gray-700 mx-2"></div>
                        <div class="shrink-0">
                            <img :src="testimonial.image" :alt="testimonial.company" class="h-8 object-contain">
                        </div>

                        <div class="ml-auto text-right">
                            <div class="text-white font-bold">{{ testimonial.name }}</div>
                            <div class="text-gray-400 text-sm">{{ testimonial.company }}</div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- CTA -->
            <div class="flex justify-center">
                <button
                    class="bg-blue-900/30 hover:bg-blue-900/50 text-white font-semibold py-3 px-8 rounded-full border border-blue-500/30 transition-all">
                    {{ $t('landingPage.v2.testimonials.button') }}
                </button>
            </div>

        </div>
    </div>
</template>

<style scoped></style>
